import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useI18n } from "../i18n/I18nProvider";


const content = {
    en: {
      title: "Terms & Conditions",
      updated: "Last updated",
      updatedDate: "July 28, 2026",

      intro:
        "These Terms and Conditions govern your access to and use of takeKONTROL. By creating an account, registering for a workshop, or using the platform, you agree to these terms. If you do not agree, you must not use the service.",

      sections: [
        {
          title: "1. Scope of the Service",
          body: [
            "takeKONTROL is a digital platform designed to support individuals and organizations in improving preparedness, resilience, and crisis awareness.",
            "The platform provides registration, company and role assignment, workshops, preparedness questions, availability checks, scenario exercises, module tasks, progress tracking, scores, recommendations, and group dashboard features.",
            "All platform content, scores, recommendations, workshop results, and other outputs are informational and educational in nature.",
            "The platform is not an emergency service and does not replace professional, legal, medical, psychological, security, civil protection, or emergency advice.",
          ],
        },
        {
          title: "2. User Accounts and Responsibilities",
          body: [
            "To access the service, users must create or use an account linked to a valid company email address or another email authorized for the platform.",
            "Users are responsible for maintaining the confidentiality of their login credentials and for all activities under their account.",
            "Users agree to provide accurate, complete, and up-to-date information during registration, profile updates, assessments, availability checks, and workshops.",
            "If anonymous registration or anonymous participation is available, users must still provide truthful non-identifying information needed for platform operation.",
            "Unauthorized use of accounts or attempts to access other users' data is strictly prohibited.",
          ],
        },
        {
          title: "3. Organizational and Employer Context",
          body: [
            "The platform may be used within an organizational context, where users are associated with a company or institution.",
            "In such cases, authorized roles such as managers, HR users, or administrators may manage workshops, invite or link employees, use manager registration codes, and view data permitted by the platform's role-based access rules.",
            "Depending on the configured features and permissions, visible data may include profile fields, department, role, participation status, workshop answers, scores, progress, recommendations, and aggregated group insights.",
            "Users acknowledge that their participation may be part of a structured company program (e.g., workshops, assessments, preparedness initiatives).",
          ],
        },
        {
          title: "4. Data Input and Generated Outputs",
          body: [
            "Users provide input through questionnaires, workshop screens, scenario decisions, module answers, task progress, availability checks, invitation workflows, profile settings, support messages, optional avatar upload, and voluntary lab photo uploads.",
            "Lab photo uploads may show the user or other people present in the lab. Users should upload photos only when they are comfortable sharing them in this workshop context and have the necessary permission from visible persons where required.",
            "Based on this input, the system may generate Kontrol scores, LAB scores, score bands, availability signals, flags, recommendations, module progress, next actions, and group statistics.",
            "These outputs are based on automated processing and predefined logic models.",
            "The accuracy and usefulness of outputs depend on the correctness and completeness of user-provided data.",
          ],
        },
        {
          title: "5. Scoring and Preparedness Disclaimer",
          body: [
            "The platform generates internal scoring metrics such as Kontrol scores, LAB scores, readiness scores, score bands, availability signals, and recommendations.",
            "These scores are estimations intended to provide guidance and awareness, not definitive evaluations.",
            "They do not represent guarantees of safety, preparedness, or risk level.",
            "Users must not rely solely on the platform when making decisions in real-world situations, emergencies, health matters, evacuation, security, or crisis response.",
          ],
        },
        {
          title: "6. Acceptable Use",
          body: [
            "Users agree to use the platform only for lawful purposes and in accordance with these Terms.",
            "It is prohibited to misuse the system, including attempting to manipulate scoring mechanisms, submit misleading data, access unauthorized data, impersonate another person, misuse invite features, upload inappropriate files or photos, or disrupt platform functionality.",
            "Users must not enter sensitive third-party personal data unless they have the right to do so and the entry is necessary for the intended platform workflow.",
            "Any abuse of the platform may result in suspension or termination of the account.",
          ],
        },
        {
          title: "7. Workshops and Group Features",
          body: [
            "Workshop leaders, managers, or admins may control workshop screens, modules, participants, active workshop state, and group dashboards.",
            "Some workshop answers, decisions, percentages, and group statistics may be shown to authorized roles or participants as part of the workshop experience.",
            "Photos voluntarily uploaded during lab photo tasks may be stored and shown to authorized managers or admins in the Lab Photos gallery.",
            "Users are responsible for participating respectfully and for avoiding disclosure of confidential, harmful, or unlawful content in free-text fields or invitation workflows.",
          ],
        },
        {
          title: "8. Account Deletion and Data Removal",
          body: [
            "Users may delete their account at any time through the platform.",
            "Accounts that are not used for one year may be automatically deleted together with linked platform data. Inactivity is measured from the latest known sign-in or session activity, or from account creation if the account was never used.",
            "Account deletion removes the authentication user and related records according to the platform's database relationships and deletion processes.",
            "Voluntarily uploaded lab photos are stored until deletion is requested, unless they are deleted earlier as part of account deletion, workshop administration, or platform maintenance.",
            "Some aggregated, anonymized, audit, backup, or legally required data may be retained where permitted or required.",
            "Deletion may affect access to workshops, scores, progress, recommendations, and organizational dashboards.",
          ],
        },
        {
          title: "9. Availability and Service Changes",
          body: [
            "The platform is provided on an 'as is' and 'as available' basis.",
            "We do not guarantee uninterrupted availability or error-free operation.",
            "We may modify, suspend, restrict, or discontinue parts of the service, features, scoring logic, workshops, modules, or access permissions at any time.",
          ],
        },
        {
          title: "10. Limitation of Liability",
          body: [
            "To the maximum extent permitted by law, takeKONTROL is not liable for indirect, incidental, consequential, special, punitive, or similar damages arising from use of or inability to use the platform.",
            "This includes decisions made based on platform outputs, workshop results, recommendations, scores, or group dashboards.",
            "Users remain fully responsible for their real-world actions and decisions.",
          ],
        },
        {
          title: "11. Intellectual Property",
          body: [
            "The platform, software, design, text, modules, questions, scoring logic, media, and related materials are owned by takeKONTROL or its licensors unless otherwise stated.",
            "Users receive a limited, revocable, non-exclusive right to use the platform for its intended purpose.",
            "Users must not copy, reverse engineer, resell, redistribute, or create competing services from platform content or logic except where allowed by law or written permission.",
          ],
        },
        {
          title: "12. Changes to Terms",
          body: [
            "We may update these Terms and Conditions from time to time to reflect platform, data model, provider, or legal changes.",
            "Continued use of the platform after changes constitutes acceptance of the updated terms.",
          ],
        },
        {
          title: "13. Contact",
          body: [
            "For questions regarding these Terms and Conditions, please contact: info@takekontrol.de",
          ],
        },
      ],
    },

    de: {
      title: "Allgemeine Nutzungsbedingungen",
      updated: "Zuletzt aktualisiert",
      updatedDate: "28. Juli 2026",

      intro:
        "Diese Nutzungsbedingungen regeln den Zugang zu und die Nutzung von takeKONTROL. Mit der Erstellung eines Kontos, der Registrierung für einen Workshop oder der Nutzung der Plattform stimmen Sie diesen Bedingungen zu. Wenn Sie nicht zustimmen, dürfen Sie den Dienst nicht nutzen.",

      sections: [
        {
          title: "1. Leistungsumfang",
          body: [
            "takeKONTROL ist eine digitale Plattform zur Förderung von Resilienz, Vorbereitung und Krisenbewusstsein.",
            "Die Plattform bietet Registrierung, Unternehmens- und Rollenzuordnung, Workshops, Preparedness-Fragen, Verfügbarkeitschecks, Szenarioübungen, Modulaufgaben, Fortschrittsverfolgung, Scores, Empfehlungen und Gruppendashboards.",
            "Alle Inhalte, Scores, Empfehlungen, Workshop-Ergebnisse und sonstigen Ausgaben dienen ausschließlich Informations- und Bildungszwecken.",
            "Die Plattform ist kein Notfalldienst und ersetzt keine professionelle, rechtliche, medizinische, psychologische, sicherheitsbezogene, zivilschutzbezogene oder Notfallberatung.",
          ],
        },
        {
          title: "2. Benutzerkonten",
          body: [
            "Für die Nutzung ist ein Konto mit gültiger Firmen-E-Mail-Adresse oder einer anderen für die Plattform zugelassenen E-Mail-Adresse erforderlich.",
            "Sie sind für die Sicherheit Ihrer Zugangsdaten verantwortlich.",
            "Angaben bei Registrierung, Profilaktualisierung, Assessments, Verfügbarkeitschecks und Workshops müssen korrekt, vollständig und aktuell sein.",
            "Wenn anonyme Registrierung oder Teilnahme verfügbar ist, müssen dennoch wahrheitsgemäße nicht identifizierende Angaben gemacht werden, die für den Betrieb erforderlich sind.",
            "Unbefugte Kontonutzung oder der Versuch, auf Daten anderer Nutzer zuzugreifen, ist untersagt.",
          ],
        },
        {
          title: "3. Nutzung im Unternehmen",
          body: [
            "Die Plattform kann im Unternehmenskontext genutzt werden.",
            "Autorisierte Rollen wie Manager, HR-Nutzer oder Admins können Workshops verwalten, Mitarbeitende einladen oder verknüpfen, Manager-Registrierungscodes nutzen und Daten gemäß den rollenbasierten Zugriffsregeln einsehen.",
            "Je nach Funktionen und Berechtigungen können Profildaten, Abteilung, Rolle, Teilnahmestatus, Workshop-Antworten, Scores, Fortschritt, Empfehlungen und aggregierte Gruppeneinblicke sichtbar sein.",
            "Nutzer erkennen an, dass ihre Teilnahme Teil eines strukturierten Unternehmensprogramms sein kann.",
          ],
        },
        {
          title: "4. Dateneingaben und Ergebnisse",
          body: [
            "Nutzer geben Daten über Fragebögen, Workshop-Ansichten, Szenarioentscheidungen, Modulantworten, Aufgabenfortschritt, Verfügbarkeitschecks, Einladungen, Profileinstellungen, Support-Nachrichten, optionalen Avatar-Upload und freiwillige Lab-Foto-Uploads ein.",
            "Lab-Foto-Uploads können den Nutzer oder andere im Lab anwesende Personen zeigen. Nutzer sollten Fotos nur hochladen, wenn sie mit der Weitergabe im Workshop-Kontext einverstanden sind und, soweit erforderlich, die Zustimmung sichtbar abgebildeter Personen haben.",
            "Auf dieser Grundlage kann das System Kontrol Scores, LAB Scores, Score-Bänder, Verfügbarkeitssignale, Flags, Empfehlungen, Modulfortschritt, nächste Schritte und Gruppenstatistiken erzeugen.",
            "Diese Ergebnisse beruhen auf automatisierter Verarbeitung und vordefinierten Logikmodellen.",
            "Richtigkeit und Nutzen hängen von der Korrektheit und Vollständigkeit der Nutzereingaben ab.",
          ],
        },
        {
          title: "5. Score- und Preparedness-Haftungsausschluss",
          body: [
            "Die Plattform erzeugt interne Kennzahlen wie Kontrol Scores, LAB Scores, Readiness Scores, Score-Bänder, Verfügbarkeitssignale und Empfehlungen.",
            "Diese Scores sind Schätzwerte zur Orientierung und Sensibilisierung, keine abschließenden Bewertungen.",
            "Sie stellen keine Garantie für Sicherheit, Vorbereitung oder Risikoniveau dar.",
            "Nutzer dürfen sich bei realen Situationen, Notfällen, Gesundheitsfragen, Evakuierung, Sicherheit oder Krisenreaktion nicht ausschließlich auf die Plattform verlassen.",
          ],
        },
        {
          title: "6. Nutzung",
          body: [
            "Nur rechtmäßige Nutzung ist erlaubt.",
            "Verboten sind insbesondere Manipulation von Scores, irreführende Eingaben, unbefugter Datenzugriff, Identitätsmissbrauch, Missbrauch von Einladungsfunktionen, ungeeignete Datei- oder Foto-Uploads und Störung der Plattform.",
            "Daten Dritter dürfen nur eingegeben werden, wenn hierzu eine Berechtigung besteht und die Eingabe für den vorgesehenen Workflow erforderlich ist.",
            "Missbrauch kann zur Sperrung oder Beendigung des Kontos führen.",
          ],
        },
        {
          title: "7. Workshops und Gruppenfunktionen",
          body: [
            "Workshop-Leitung, Manager oder Admins können Workshop-Ansichten, Module, Teilnehmende, aktive Workshop-Zustände und Gruppendashboards steuern.",
            "Bestimmte Workshop-Antworten, Entscheidungen, Prozentwerte und Gruppenstatistiken können autorisierten Rollen oder Teilnehmenden im Rahmen des Workshops angezeigt werden.",
            "Während Lab-Fotoaufgaben freiwillig hochgeladene Fotos können gespeichert und autorisierten Manager- oder Admin-Rollen in der Lab-Fotos-Galerie angezeigt werden.",
            "Nutzer nehmen respektvoll teil und dürfen keine vertraulichen, schädlichen oder rechtswidrigen Inhalte in Freitextfelder oder Einladungsfunktionen eingeben.",
          ],
        },
        {
          title: "8. Löschung",
          body: [
            "Konten können über die Plattform gelöscht werden, soweit diese Funktion verfügbar ist.",
            "Konten, die ein Jahr lang nicht genutzt werden, können automatisch zusammen mit den verknüpften Plattformdaten gelöscht werden. Inaktivität wird anhand der letzten bekannten Anmeldung oder Sitzungsaktivität gemessen; bei nie genutzten Konten anhand des Erstellungsdatums.",
            "Die Löschung entfernt den Authentifizierungsnutzer und zugehörige Datensätze gemäß den Datenbankbeziehungen und Löschprozessen der Plattform.",
            "Freiwillig hochgeladene Lab-Fotos werden gespeichert, bis deren Löschung angefordert wird, sofern sie nicht früher im Rahmen einer Kontolöschung, Workshopverwaltung oder Plattformwartung gelöscht werden.",
            "Aggregierte, anonymisierte, Audit-, Backup- oder gesetzlich erforderliche Daten können, soweit zulässig oder erforderlich, gespeichert bleiben.",
            "Eine Löschung kann den Zugriff auf Workshops, Scores, Fortschritt, Empfehlungen und Organisations-Dashboards beeinflussen.",
          ],
        },
        {
          title: "9. Verfügbarkeit",
          body: [
            "Die Plattform wird wie verfügbar bereitgestellt.",
            "Wir garantieren keine unterbrechungsfreie Verfügbarkeit oder fehlerfreie Funktion.",
            "Funktionen, Scoring-Logik, Workshops, Module oder Zugriffsrechte können jederzeit geändert, ausgesetzt, eingeschränkt oder eingestellt werden.",
          ],
        },
        {
          title: "10. Haftung",
          body: [
            "Soweit gesetzlich zulässig, haftet takeKONTROL nicht für indirekte, zufällige, Folge-, Sonder-, Straf- oder ähnliche Schäden aus Nutzung oder Nichtnutzung der Plattform.",
            "Dies umfasst Entscheidungen aufgrund von Plattformausgaben, Workshop-Ergebnissen, Empfehlungen, Scores oder Gruppendashboards.",
            "Nutzer bleiben für ihre realen Handlungen und Entscheidungen selbst verantwortlich.",
          ],
        },
        {
          title: "11. Geistiges Eigentum",
          body: [
            "Plattform, Software, Design, Texte, Module, Fragen, Scoring-Logik, Medien und zugehörige Materialien gehören takeKONTROL oder den jeweiligen Lizenzgebern, sofern nicht anders angegeben.",
            "Nutzer erhalten ein beschränktes, widerrufliches, nicht ausschließliches Recht zur bestimmungsgemäßen Nutzung der Plattform.",
            "Kopieren, Reverse Engineering, Weiterverkauf, Weiterverbreitung oder der Aufbau konkurrierender Dienste auf Basis von Plattforminhalten oder Logik ist untersagt, soweit nicht gesetzlich erlaubt oder schriftlich genehmigt.",
          ],
        },
        {
          title: "12. Änderungen",
          body: [
            "Wir können diese Nutzungsbedingungen aktualisieren, um Änderungen an Plattform, Datenmodell, Dienstleistern oder rechtlichen Anforderungen abzubilden.",
            "Die weitere Nutzung nach Änderungen gilt als Zustimmung zu den aktualisierten Bedingungen.",
          ],
        },
        {
          title: "13. Kontakt",
          body: ["info@takekontrol.de"],
        },
      ],
    },
  };

type Props = {
  handlePageChange: (page: string) => void;
  role: string;
};

export default function TermsOfService(props: Props) {
  const { lang } = useI18n();
  const t = content[lang === "de" ? "de" : "en"];

  const { handlePageChange, role } = props;

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box 
      sx={{
        position: "relative",
        minHeight: "100vh",

        // 🌌 SAME BACKGROUND AS WORKSHOP
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      {/* 🔙 BACK BUTTON */}
      <Box
        sx={{
          position: "absolute",
          top: isTablet ? 12 : 24,
          left: isTablet ? 12 : 24,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => {
            if (role === "admin") {
              handlePageChange("ControlPanel");
            } else {
              handlePageChange("JourneyPillars");
            }
          }}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 0 15px rgba(255,255,255,0.1)",

            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <HomeOutlinedIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          px: isTablet ? 2 : 4,
          py: isTablet ? 4 : 8,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 5,
            p: isTablet ? 3 : 5,

            // ✨ GLASS CARD
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",

            backdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",

            color: "white",
          }}
        >
          {/* TITLE */}
          <Typography
            variant={isTablet ? "h5" : "h4"}
            fontWeight={800}
            mb={2}
          >
            {t.title}
          </Typography>

          {/* UPDATED */}
          <Typography
            variant="body2"
            sx={{ opacity: 0.6, mb: 3 }}
          >
            {t.updated}: {t.updatedDate}
          </Typography>

          {/* INTRO */}
          <Typography sx={{ opacity: 0.9, mb: 3 }}>
            {t.intro}
          </Typography>

          {/* SECTIONS */}
          <Stack spacing={3}>
            {t.sections.map((section, i) => (
              <Box key={i}>
                <Typography variant="h6">{section.title}</Typography>
                {section.body.map((line, j) => (
                  <Typography key={j}>{line}</Typography>
                ))}
              </Box>
            ))}
            </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
